import { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { User, Lock, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";


export default function Perfil({ onLogout }) {
  const [loading, setLoading] = useState(false);
  const [escolaData, setEscolaData] = useState(null);
  const [formData, setFormData] = useState({
    senha_atual: "",
    nova_senha: "",
    confirmar_senha: ""
  });
  const [escolaForm, setEscolaForm] = useState({
    nome_escola: "",
    dados_escola: ""
  });
  const [pagePasswords, setPagePasswords] = useState({
    alunos: { hasPassword: false, password: "" },
    mensalidades: { hasPassword: false, password: "" },
    relatorios: { hasPassword: false, password: "" }
  });
  const [confirmRemove, setConfirmRemove] = useState({ show: false, page: "", password: "" });
  
  // Login Auxiliar
  const [loginsAuxiliares, setLoginsAuxiliares] = useState([]);
  const [showCreateAuxiliar, setShowCreateAuxiliar] = useState(false);
  const [auxiliarForm, setAuxiliarForm] = useState({
    username: "",
    password: ""
  });

  // Templates WhatsApp
  const [whatsappTemplates, setWhatsappTemplates] = useState({
    a_vencer: "",
    vencida: ""
  });

  const username = localStorage.getItem('username');

  useEffect(() => {
    fetchEscolaData();
    fetchPagePasswordsStatus();
    fetchLoginsAuxiliares();
    fetchWhatsappTemplates();

    // Cleanup ao desmontar - fechar todos os dialogs
    return () => {
      setConfirmRemove({ show: false, page: "", password: "" });
      setShowCreateAuxiliar(false);
    };
  }, []);

  const fetchEscolaData = async () => {
    try {
      const response = await api.get('/perfil/escola');
      setEscolaData(response.data);
      setEscolaForm({
        nome_escola: response.data.nome_escola || "",
        dados_escola: response.data.dados_escola || ""
      });
    } catch (error) {
      console.error("Erro ao carregar dados da escola");
    }
  };

  const fetchPagePasswordsStatus = async () => {
    try {
      const response = await api.get('/perfil/page-passwords-status');
      
      // Atualizar estado com as senhas existentes
      setPagePasswords({
        alunos: { hasPassword: response.data.alunos, password: "" },
        mensalidades: { hasPassword: response.data.mensalidades, password: "" },
        relatorios: { hasPassword: response.data.relatorios, password: "" }
      });
    } catch (error) {
      console.error("Erro ao carregar status das senhas de páginas");
    }
  };

  const fetchLoginsAuxiliares = async () => {
    try {
      const response = await api.get('/auxiliar/list');
      setLoginsAuxiliares(response.data);
    } catch (error) {
      console.error("Erro ao carregar logins auxiliares");
    }
  };

  const handleCreateAuxiliar = async () => {
    if (!auxiliarForm.username || !auxiliarForm.password) {
      toast.error("Preencha todos os campos");
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(auxiliarForm.username)) {
      toast.error("O nome de usuário deve ser um email válido");
      return;
    }

    if (auxiliarForm.password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      await api.post('/auxiliar/create', auxiliarForm);

      toast.success("Login auxiliar criado com sucesso!");
      flushSync(() => {
        setShowCreateAuxiliar(false);
        setAuxiliarForm({ username: "", password: "" });
      });
      fetchLoginsAuxiliares();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar login auxiliar");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAuxiliar = async (loginId, loginUsername) => {
    if (!window.confirm(`Deseja realmente excluir o login "${loginUsername}"?`)) {
      return;
    }

    try {
      await api.delete(`/auxiliar/delete/${loginId}`);

      toast.success("Login auxiliar excluído!");
      fetchLoginsAuxiliares();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir login auxiliar");
    }
  };

  const fetchWhatsappTemplates = async () => {
    try {
      const response = await api.get('/perfil/whatsapp-templates');
      setWhatsappTemplates(response.data);
    } catch (error) {
      console.error("Erro ao carregar templates WhatsApp");
    }
  };

  const handleSaveWhatsappTemplates = async () => {
    setLoading(true);
    try {
      await api.post('/perfil/whatsapp-templates', whatsappTemplates);

      toast.success("Templates WhatsApp salvos com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar templates");
    } finally {
      setLoading(false);
    }
  };

  const handlePagePasswordSubmit = async (page) => {
    const pageData = pagePasswords[page];
    
    // Validar se o usuário digitou uma senha
    if (!pageData.password) {
      toast.error("Digite uma senha para proteger a página");
      return;
    }
    
    // Criar nova senha
    try {
      await api.post('/page-password/set', {
        page: page,
        password: pageData.password,
        enabled: true
      });
      
      toast.success(`Senha ativada para ${page}!`);
      
      // Atualizar estado para refletir que agora há senha
      flushSync(() => {
        setPagePasswords(prev => ({
          ...prev,
          [page]: { hasPassword: true, password: "" }
        }));
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao configurar senha");
    }
  };

  const handleRemovePassword = (page) => {
    // Abrir dialog de confirmação com pequeno delay para evitar race condition
    setTimeout(() => {
      setConfirmRemove({ show: true, page: page, password: "" });
    }, 0);
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove.password) {
      toast.error("Digite a senha atual para remover");
      return;
    }

    try {
      // Primeiro verificar se a senha está correta
      await api.post(`/page-password/verify?page=${confirmRemove.page}&password=${confirmRemove.password}`, {});

      // Senha correta, remover proteção
      await api.post('/page-password/set', {
        page: confirmRemove.page,
        password: "",
        enabled: false
      });
      
      toast.success(`Senha removida de ${confirmRemove.page}!`);
      flushSync(() => {
        setConfirmRemove({ show: false, page: "", password: "" });
        
        // Atualizar estado para refletir que não há mais senha
        setPagePasswords(prev => ({
          ...prev,
          [confirmRemove.page]: { hasPassword: false, password: "" }
        }));
      });
    } catch (error) {
      toast.error("Senha incorreta");
      setConfirmRemove(prev => ({ ...prev, password: "" }));
    }
  };

  const handleEscolaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put('/perfil/escola', escolaForm);
      toast.success("Dados da escola atualizados!");
      fetchEscolaData();
    } catch (error) {
      toast.error("Erro ao atualizar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validações no frontend
    if (formData.nova_senha.length < 6) {
      toast.error("A nova senha deve ter no mínimo 6 caracteres");
      return;
    }

    if (formData.nova_senha !== formData.confirmar_senha) {
      toast.error("As senhas não coincidem");
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/alterar-senha', {
        senha_atual: formData.senha_atual,
        nova_senha: formData.nova_senha
      });

      toast.success("Senha alterada com sucesso!");
      
      // Limpar formulário
      setFormData({
        senha_atual: "",
        nova_senha: "",
        confirmar_senha: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Meu Perfil</h1>
          <p className="text-gray-600">Gerencie suas informações e configurações</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle>Informações da Conta</CardTitle>
                <CardDescription className="mt-1">Usuário: <strong>{username}</strong></CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {escolaData && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                <CardTitle>Dados da Escola</CardTitle>
              </div>
              <CardDescription>Personalize as informações da sua escola</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEscolaSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_escola">Nome da Escola</Label>
                  <Input
                    id="nome_escola"
                    value={escolaForm.nome_escola}
                    onChange={(e) => setEscolaForm({...escolaForm, nome_escola: e.target.value})}
                    placeholder="Ex: Escola Esportiva ABC"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dados_escola">Informações Adicionais</Label>
                  <Textarea
                    id="dados_escola"
                    value={escolaForm.dados_escola}
                    onChange={(e) => setEscolaForm({...escolaForm, dados_escola: e.target.value})}
                    placeholder="Endereço, telefone, horários, etc."
                    rows={3}
                  />
                </div>
                <Button type="submit" disabled={loading}>
                  {loading ? "Salvando..." : "Salvar Dados da Escola"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Card de Login Auxiliar */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Logins Auxiliares (Somente Chamada)
            </CardTitle>
            <CardDescription>Crie logins com acesso limitado apenas à página de Chamada</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loginsAuxiliares.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <User className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 mb-4">Nenhum login auxiliar criado</p>
                <Button onClick={() => setShowCreateAuxiliar(true)}>
                  Criar Login Auxiliar
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {loginsAuxiliares.map((login) => (
                    <div key={login.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold">{login.username}</p>
                          <p className="text-xs text-gray-500">
                            Criado em: {new Date(login.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteAuxiliar(login.id, login.username)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ))}
                </div>
                <Button onClick={() => setShowCreateAuxiliar(true)} className="w-full">
                  Criar Novo Login Auxiliar
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Card de Templates WhatsApp */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Mensagens WhatsApp
            </CardTitle>
            <CardDescription>Personalize as mensagens de cobrança enviadas via WhatsApp</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Template A Vencer */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Mensagem: Mensalidade a Vencer</Label>
                <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                  A Vencer
                </Badge>
              </div>
              <Textarea
                value={whatsappTemplates.a_vencer}
                onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, a_vencer: e.target.value })}
                rows={8}
                className="font-mono text-sm"
              />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Variáveis disponíveis:</strong> {"{responsavel}"}, {"{aluno}"}, {"{dia_vencimento}"}, {"{dias_faltam}"}, {"{valor}"}
                </p>
              </div>
            </div>

            {/* Template Vencida */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Mensagem: Mensalidade Vencida</Label>
                <Badge className="bg-red-100 text-red-800 border-red-300">
                  Vencida
                </Badge>
              </div>
              <Textarea
                value={whatsappTemplates.vencida}
                onChange={(e) => setWhatsappTemplates({ ...whatsappTemplates, vencida: e.target.value })}
                rows={8}
                className="font-mono text-sm"
              />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Variáveis disponíveis:</strong> {"{responsavel}"}, {"{aluno}"}, {"{dia_vencimento}"}, {"{dias_atraso}"}, {"{valor}"}
                </p>
              </div>
            </div>

            <Button
              onClick={handleSaveWhatsappTemplates}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading ? "Salvando..." : "Salvar Templates WhatsApp"}
            </Button>
          </CardContent>
        </Card>

        {/* Card de Senhas de Páginas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Proteger Páginas com Senha
            </CardTitle>
            <CardDescription>Configure senhas para restringir acesso a páginas específicas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {['alunos', 'mensalidades', 'relatorios'].map((page) => (
              <div key={page} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold capitalize">{page}</h4>
                  {pagePasswords[page].hasPassword ? (
                    <span className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                      ✓ Senha Ativada
                    </span>
                  ) : (
                    <span className="text-sm px-3 py-1 bg-gray-100 text-gray-600 rounded-full">
                      Sem Proteção
                    </span>
                  )}
                </div>
                
                {pagePasswords[page].hasPassword ? (
                  // Se já existe senha: mostrar apenas botão de remover
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Esta página está protegida por senha. Para definir uma nova senha, você precisa remover a senha atual primeiro.
                    </p>
                    <Button
                      onClick={() => handleRemovePassword(page)}
                      variant="destructive"
                      size="sm"
                      className="w-full"
                    >
                      Remover Senha
                    </Button>
                  </div>
                ) : (
                  // Se não existe senha: permitir criar uma
                  <div className="space-y-2">
                    <Label htmlFor={`password-${page}`}>Nova Senha</Label>
                    <div className="flex gap-2">
                      <Input
                        id={`password-${page}`}
                        type="password"
                        placeholder="Digite a senha"
                        value={pagePasswords[page].password}
                        onChange={(e) => setPagePasswords(prev => ({
                          ...prev,
                          [page]: { ...prev[page], password: e.target.value }
                        }))}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => handlePagePasswordSubmit(page)}
                        size="sm"
                      >
                        Salvar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-500 flex items-start gap-2">
              <span>⚠️</span>
              <span>Quando ativado, será solicitada senha sempre ao acessar estas páginas</span>
            </p>
          </CardContent>
        </Card>

        {/* Card de Alteração de Senha */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <CardTitle>Alterar Senha</CardTitle>
            </div>
            <CardDescription>Mantenha sua conta segura alterando sua senha regularmente</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="senha_atual">Senha Atual *</Label>
                <Input
                  id="senha_atual"
                  data-testid="senha-atual-input"
                  type="password"
                  value={formData.senha_atual}
                  onChange={(e) => setFormData({ ...formData, senha_atual: e.target.value })}
                  required
                  placeholder="Digite sua senha atual"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nova_senha">Nova Senha *</Label>
                <Input
                  id="nova_senha"
                  data-testid="nova-senha-input"
                  type="password"
                  value={formData.nova_senha}
                  onChange={(e) => setFormData({ ...formData, nova_senha: e.target.value })}
                  required
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmar_senha">Confirmar Nova Senha *</Label>
                <Input
                  id="confirmar_senha"
                  data-testid="confirmar-senha-input"
                  type="password"
                  value={formData.confirmar_senha}
                  onChange={(e) => setFormData({ ...formData, confirmar_senha: e.target.value })}
                  required
                  placeholder="Digite novamente a nova senha"
                />
              </div>

              <div className="pt-4">
                <Button
                  data-testid="alterar-senha-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
                >
                  {loading ? "Alterando..." : "Alterar Senha"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Confirmação para Remover Senha */}
      <Dialog key="confirm-remove-password-dialog" open={confirmRemove.show} onOpenChange={(open) => !open && setConfirmRemove({ show: false, page: "", password: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Confirmar Remoção de Senha
            </DialogTitle>
            <DialogDescription>
              Digite a senha atual da página <span className="font-semibold capitalize">{confirmRemove.page}</span> para remover a proteção
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Senha Atual</Label>
            <Input
              type="password"
              placeholder="Digite a senha atual"
              value={confirmRemove.password}
              onChange={(e) => setConfirmRemove(prev => ({ ...prev, password: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmRemove()}
              className="mt-2"
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmRemove({ show: false, page: "", password: "" })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmRemove}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirmar Remoção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Criar Login Auxiliar */}
      <Dialog key="create-auxiliar-dialog" open={showCreateAuxiliar} onOpenChange={setShowCreateAuxiliar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Login Auxiliar</DialogTitle>
            <DialogDescription>
              Este login terá acesso limitado apenas à página de Chamada. Ideal para professores ou assistentes que apenas registram presença.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="auxiliar-username">Email *</Label>
              <Input
                id="auxiliar-username"
                type="email"
                value={auxiliarForm.username}
                onChange={(e) => setAuxiliarForm({ ...auxiliarForm, username: e.target.value })}
                placeholder="professor@escola.com"
              />
              <p className="text-xs text-gray-500">O login auxiliar deve usar um email válido</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auxiliar-password">Senha *</Label>
              <Input
                id="auxiliar-password"
                type="password"
                value={auxiliarForm.password}
                onChange={(e) => setAuxiliarForm({ ...auxiliarForm, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                ℹ️ <strong>Permissões do Login Auxiliar:</strong>
                <br />✓ Acesso apenas à página de Chamada
                <br />✗ Não pode acessar Alunos, Turmas, Mensalidades ou Administração
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateAuxiliar(false);
                setAuxiliarForm({ username: "", password: "" });
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAuxiliar}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? "Criando..." : "Criar Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
